import { Outlet, useMatch } from 'react-router'
import { useEffect, useState } from 'react'
import { AppShell } from '@/components/layout/AppShell'
import { useRoomStore } from '@/lib/store/room-store'
import { useAuthStore } from '@/lib/store/auth-store'
import { useUIStore } from '@/lib/store/ui-store'
import { Spinner } from '@/components/ui/Spinner'
import { SearchPanel } from '@/components/search/SearchPanel'
import { ThreadsListView } from '@/components/timeline/ThreadsListView'
import { RoomCreateDialog } from '@/components/rooms/RoomCreateDialog'
import { NewDirectMessageDialog } from '@/components/rooms/NewDirectMessageDialog'
import { KeyRestoreDialog } from '@/components/encryption/KeyRestoreDialog'
import { DeviceVerificationDialog } from '@/components/encryption/DeviceVerificationDialog'
import { getClient } from '@/lib/matrix/client'
import { CryptoEvent } from 'matrix-js-sdk/lib/crypto-api'
import type { VerificationRequest } from 'matrix-js-sdk/lib/crypto-api'
import { CodexAuthDialog } from '@/components/ui/CodexAuthDialog'
import { setupNotificationListener } from '@/lib/matrix/notifications'
import { setupTabBadge } from '@/lib/matrix/tab-badge'

function EmptyState() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <svg
          className="mx-auto mb-4 text-[rgb(var(--np-tx-muted))]"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <p className="text-[rgb(var(--np-tx-muted))] text-sm">
          Select a room to start chatting
        </p>
      </div>
    </div>
  )
}

function SyncingState() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <Spinner size={32} className="mx-auto mb-4" />
        <p className="text-sm text-[rgb(var(--np-tx-muted))]">Syncing...</p>
      </div>
    </div>
  )
}

export function ChatPage() {
  const roomMatch = useMatch('/room/:roomId')
  const { setActiveRoom } = useRoomStore()
  const { isSyncing, userId } = useAuthStore()
  const threadsViewOpen = useUIStore((s) => s.threadsViewOpen)
  const setThreadsViewOpen = useUIStore((s) => s.setThreadsViewOpen)
  const [showSearch, setShowSearch] = useState(false)
  const [showCreateRoom, setShowCreateRoom] = useState(false)
  const [showNewDm, setShowNewDm] = useState(false)
  const [showKeyRestore, setShowKeyRestore] = useState(false)
  const [showSecurity, setShowSecurity] = useState(false)
  const [incomingVerification, setIncomingVerification] = useState<VerificationRequest | null>(null)
  const [showCodexAuth, setShowCodexAuth] = useState(false)
  const [aiAllowed, setAiAllowed] = useState(false)

  useEffect(() => {
    setActiveRoom(roomMatch?.params.roomId ?? null)
  }, [roomMatch?.params.roomId, setActiveRoom])

  useEffect(() => {
    setupNotificationListener()
    setupTabBadge()
  }, [])

  // Surface incoming device-verification requests by opening the security dialog.
  useEffect(() => {
    const client = getClient()
    if (!client) return
    const handler = (request: VerificationRequest) => {
      setIncomingVerification(request)
      setShowSecurity(true)
    }
    client.on(CryptoEvent.VerificationRequestReceived, handler)
    return () => {
      client.off(CryptoEvent.VerificationRequestReceived, handler)
    }
  }, [])

  useEffect(() => {
    if (!userId) return
    fetch(`/api/codex/check-access?user=${encodeURIComponent(userId)}`)
      .then((r) => r.json())
      .then((d: { allowed: boolean }) => setAiAllowed(d.allowed))
      .catch(() => setAiAllowed(false))
  }, [userId])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setShowSearch((v) => !v)
      }
      if (e.key === 'Escape') {
        setShowSearch(false)
        setShowCreateRoom(false)
        setShowNewDm(false)
        setShowKeyRestore(false)
        setShowSecurity(false)
        setShowCodexAuth(false)
        setThreadsViewOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [setThreadsViewOpen])

  return (
    <>
      <AppShell
        onSearchOpen={() => setShowSearch(true)}
        onCreateRoom={() => setShowCreateRoom(true)}
        onNewDirectMessage={() => setShowNewDm(true)}
        onKeyRestore={() => setShowKeyRestore(true)}
        onSecurity={() => setShowSecurity(true)}
        onCodexAuth={aiAllowed ? () => setShowCodexAuth(true) : undefined}
      >
        {isSyncing ? (
          <SyncingState />
        ) : roomMatch ? (
          <Outlet />
        ) : (
          <EmptyState />
        )}
      </AppShell>

      {showSearch && <SearchPanel onClose={() => setShowSearch(false)} />}
      {threadsViewOpen && <ThreadsListView onClose={() => setThreadsViewOpen(false)} />}
      {showCreateRoom && <RoomCreateDialog onClose={() => setShowCreateRoom(false)} />}
      {showNewDm && <NewDirectMessageDialog onClose={() => setShowNewDm(false)} />}
      {showKeyRestore && <KeyRestoreDialog onClose={() => setShowKeyRestore(false)} />}
      {showSecurity && (
        <DeviceVerificationDialog
          incomingRequest={incomingVerification}
          onClose={() => {
            setShowSecurity(false)
            setIncomingVerification(null)
          }}
        />
      )}
      {showCodexAuth && <CodexAuthDialog onClose={() => setShowCodexAuth(false)} />}
    </>
  )
}
